import React, { useMemo } from 'react';

import { Icon } from '../Icon';
import { colors } from '../../styles/tokens';
import { Buttons, Footer, Info, PageButton } from './styles';

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  itemLabel: string;
  onPageChange: (page: number) => void;
}

const MAX_VISIBLE_PAGES = 5;

const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  total,
  pageSize,
  itemLabel,
  onPageChange,
}) => {
  const pageNumbers = useMemo(() => {
    if (totalPages <= MAX_VISIBLE_PAGES) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(MAX_VISIBLE_PAGES / 2);
    let start = Math.max(1, page - half);
    const end = Math.min(totalPages, start + MAX_VISIBLE_PAGES - 1);

    start = Math.max(1, end - MAX_VISIBLE_PAGES + 1);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [page, totalPages]);

  const rangeStart = total ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <Footer>
      <Info>
        Mostrando{' '}
        <strong>
          {rangeStart}-{rangeEnd}
        </strong>{' '}
        de {total} {itemLabel}
      </Info>

      <Buttons>
        <PageButton
          type="button"
          disabled={page === 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          <Icon name="chevron_left" size={18} color={colors.onSurfaceVariant} />
        </PageButton>

        {pageNumbers.map(number => (
          <PageButton
            key={number}
            type="button"
            active={number === page}
            onClick={() => onPageChange(number)}
          >
            {number}
          </PageButton>
        ))}

        <PageButton
          type="button"
          disabled={page === totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        >
          <Icon
            name="chevron_right"
            size={18}
            color={colors.onSurfaceVariant}
          />
        </PageButton>
      </Buttons>
    </Footer>
  );
};

export { Pagination };
